"use client";

/**
 * 블록 드래그 핸들(좌측 ⠿) 드롭다운 메뉴의 커스텀 버전.
 *
 * BlockNote 기본 DragHandleMenu(삭제/색/행 제목/열 제목)를 그대로 재현하고,
 * **표 블록일 때 "표 정렬"(좌/가운데/우) 항목을 추가**한다.
 * (표 정렬을 셀 grip 메뉴가 아니라 블록 메뉴로 노출하기 위함.)
 *
 * 기본 DragHandleMenu는 children을 주면 기본 항목 대신 children만 렌더하므로,
 * 기본 항목들을 명시적으로 재구성한 뒤 정렬 항목을 덧붙인다.
 * (TableRowHeaderItem/TableColumnHeaderItem은 비-표 블록에서 자동으로 null을 반환한다.)
 */

import {
  DragHandleMenu,
  RemoveBlockItem,
  BlockColorsItem,
  TableRowHeaderItem,
  TableColumnHeaderItem,
  useComponentsContext,
  useBlockNoteEditor,
  useDictionary,
  type DragHandleMenuProps,
} from "@blocknote/react";
import type { TableAlignment } from "../extensions/TableAlignmentExtension";
import {
  getTableAlignment,
  setTableAlignment,
} from "../utils/prosemirror-table-utils";

const TABLE_ALIGN_ICONS: Record<TableAlignment, JSX.Element> = {
  left: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="1.5" y="4" width="7" height="8" rx="1" fill="currentColor" stroke="none" />
      <line x1="1.5" y1="1.5" x2="14.5" y2="1.5" />
      <line x1="1.5" y1="14.5" x2="14.5" y2="14.5" />
    </svg>
  ),
  center: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="4.5" y="4" width="7" height="8" rx="1" fill="currentColor" stroke="none" />
      <line x1="1.5" y1="1.5" x2="14.5" y2="1.5" />
      <line x1="1.5" y1="14.5" x2="14.5" y2="14.5" />
    </svg>
  ),
  right: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="7.5" y="4" width="7" height="8" rx="1" fill="currentColor" stroke="none" />
      <line x1="1.5" y1="1.5" x2="14.5" y2="1.5" />
      <line x1="1.5" y1="14.5" x2="14.5" y2="14.5" />
    </svg>
  ),
};

const TABLE_ALIGN_LABELS: Record<TableAlignment, string> = {
  left: "표 왼쪽 정렬",
  center: "표 가운데 정렬",
  right: "표 오른쪽 정렬",
};

/** 표 블록일 때만 "표 정렬" 항목(좌/가운데/우)을 렌더. table 노드 attr(tableAlignment)에 적용. */
function TableAlignmentItems(props: { block: any }) {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor();

  if (props.block?.type !== "table" || !props.block?.id) {
    return null;
  }

  const current = getTableAlignment(editor, props.block.id) as TableAlignment;

  return (
    <>
      {(["left", "center", "right"] as TableAlignment[]).map((align) => (
        <Components.Generic.Menu.Item
          key={`tableAlign-${align}`}
          icon={TABLE_ALIGN_ICONS[align]}
          checked={current === align}
          onClick={() => {
            setTableAlignment(editor, props.block.id, align);
            editor.setTextCursorPosition(props.block.id);
          }}
        >
          {TABLE_ALIGN_LABELS[align]}
        </Components.Generic.Menu.Item>
      ))}
    </>
  );
}

export const LumirDragHandleMenu = (props: DragHandleMenuProps) => {
  const dict = useDictionary();

  return (
    <DragHandleMenu {...props}>
      <RemoveBlockItem {...props}>
        {dict.drag_handle.delete_menuitem}
      </RemoveBlockItem>
      <BlockColorsItem {...props}>
        {dict.drag_handle.colors_menuitem}
      </BlockColorsItem>
      <TableRowHeaderItem {...(props as any)}>
        {dict.drag_handle.header_row_menuitem}
      </TableRowHeaderItem>
      <TableColumnHeaderItem {...(props as any)}>
        {dict.drag_handle.header_column_menuitem}
      </TableColumnHeaderItem>
      <TableAlignmentItems block={props.block} />
    </DragHandleMenu>
  );
};
